/**
 * CSV Loader Module
 * Handles loading and parsing CSV files with coordinate data
 */

// Common coordinate field name variations (case-insensitive)
const LATITUDE_FIELDS = [
    'latitude', 'lat', 'latt', 'latitd', 'latitud', 'latit',
    'y', 'y_coord', 'ycoord', 'y_coordinate', 'y-coord',
    'northing', 'north',
    'point_y', 'point-y', 'pointy',
    'geo_lat', 'geo-lat', 'geolat',
    'lat_dd', 'lat-dd', 'latdd',
    'latitude_dd', 'latitude-dd',
    'dec_lat', 'dec-lat', 'declat'
];

const LONGITUDE_FIELDS = [
    'longitude', 'long', 'lon', 'lng', 'longitud', 'longi', 'longt',
    'x', 'x_coord', 'xcoord', 'x_coordinate', 'x-coord',
    'easting', 'east',
    'point_x', 'point-x', 'pointx',
    'geo_lon', 'geo-lon', 'geolon', 'geo_lng', 'geo-lng', 'geolng',
    'lon_dd', 'lon-dd', 'londd', 'lng_dd', 'lng-dd', 'lngdd',
    'longitude_dd', 'longitude-dd',
    'dec_lon', 'dec-lon', 'declon', 'dec_lng', 'dec-lng', 'declng'
];

/**
 * Detect delimiter (comma, tab, or pipe)
 * @param {string} line - Sample line from file
 * @returns {string} Detected delimiter
 */
function detectDelimiter(line) {
    const commaCount = (line.match(/,/g) || []).length;
    const tabCount = (line.match(/\t/g) || []).length;
    const pipeCount = (line.match(/\|/g) || []).length;

    // Use whichever delimiter appears most
    if (pipeCount > commaCount && pipeCount > tabCount) {
        return '|';
    } else if (tabCount > commaCount) {
        return '\t';
    } else {
        return ',';
    }
}

/**
 * Parse CSV/TXT text into rows
 * @param {string} text - CSV/TXT text content
 * @returns {Object} Parsed data with headers and rows
 */
function parseCSV(text) {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) {
        throw new Error('File is empty');
    }

    // Detect delimiter from first line
    const delimiter = detectDelimiter(lines[0]);

    // Parse header
    const headers = parseCSVLine(lines[0], delimiter);

    // Parse rows
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i], delimiter);
        if (values.length === headers.length) {
            const row = {};
            headers.forEach((header, index) => {
                row[header] = values[index];
            });
            rows.push(row);
        }
    }

    return { headers, rows };
}

/**
 * Parse a single CSV/TXT line, handling quoted values
 * @param {string} line - CSV/TXT line
 * @param {string} delimiter - Delimiter character (comma or tab)
 * @returns {Array} Array of values
 */
function parseCSVLine(line, delimiter = ',') {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === delimiter && !inQuotes) {
            values.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    values.push(current.trim());
    return values;
}

/**
 * Detect coordinate fields in headers with flexible matching
 * @param {Array} headers - CSV headers
 * @returns {Object} Object with latField and lonField
 */
function detectCoordinateFields(headers) {
    let latField = null;
    let lonField = null;

    // Helper function to normalize field names
    const normalize = (str) => str.toLowerCase().trim().replace(/[\s_-]/g, '');

    // Strategy 1: Exact match (case-insensitive)
    for (const header of headers) {
        const normalized = normalize(header);

        if (!latField && LATITUDE_FIELDS.some(field => normalize(field) === normalized)) {
            latField = header;
        }

        if (!lonField && LONGITUDE_FIELDS.some(field => normalize(field) === normalized)) {
            lonField = header;
        }

        if (latField && lonField) break;
    }

    // Strategy 2: Partial match - contains key terms
    if (!latField || !lonField) {
        for (const header of headers) {
            const normalized = normalize(header);

            // Look for latitude indicators
            if (!latField) {
                if (normalized.includes('lat') ||
                    normalized === 'y' ||
                    normalized.includes('north')) {
                    latField = header;
                }
            }

            // Look for longitude indicators
            if (!lonField) {
                if (normalized.includes('lon') ||
                    normalized.includes('lng') ||
                    normalized === 'x' ||
                    normalized.includes('east')) {
                    lonField = header;
                }
            }

            if (latField && lonField) break;
        }
    }

    // Strategy 3: Numeric field detection for simple X/Y or lat/lon pairs
    if (!latField || !lonField) {
        const numericFields = headers.filter((header, index) => {
            const normalized = normalize(header);
            // Check if field name suggests coordinates
            return normalized.length <= 3 &&
                   (normalized === 'x' || normalized === 'y' ||
                    normalized.includes('lat') || normalized.includes('lon') ||
                    /^[0-9]+$/.test(normalized)); // numeric column names
        });

        if (numericFields.length >= 2 && (!latField || !lonField)) {
            // If we have fields like "0" and "1" or simple numeric indices
            if (!latField && !lonField) {
                latField = numericFields[1]; // Usually Y/lat comes second
                lonField = numericFields[0]; // Usually X/lon comes first
            }
        }
    }

    return { latField, lonField };
}

/**
 * Convert CSV data to GeoJSON
 * @param {Object} csvData - Parsed CSV data
 * @param {string} latField - Latitude field name
 * @param {string} lonField - Longitude field name
 * @returns {Object} GeoJSON FeatureCollection
 */
function csvToGeoJSON(csvData, latField, lonField) {
    const features = [];

    for (let i = 0; i < csvData.rows.length; i++) {
        const row = csvData.rows[i];
        const lat = parseFloat(row[latField]);
        const lon = parseFloat(row[lonField]);

        // Skip rows with invalid coordinates
        if (isNaN(lat) || isNaN(lon)) {
            console.warn(`Row ${i + 1} has invalid coordinates, skipping`);
            continue;
        }

        // Validate WGS84 bounds
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
            console.warn(`Row ${i + 1} has out-of-bounds coordinates (${lat}, ${lon}), skipping`);
            continue;
        }

        // Create properties (all columns except coordinates)
        const properties = { ...row };

        // Add feature index
        properties.__featureIndex = features.length;

        const feature = {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [lon, lat] // GeoJSON uses [longitude, latitude]
            },
            properties
        };

        features.push(feature);
    }

    return {
        type: 'FeatureCollection',
        features
    };
}

/**
 * Load and parse CSV/TXT/RACAS file
 * @param {File} file - CSV, TXT, or RACAS file
 * @param {Function} progressCallback - Progress callback (progress, message)
 * @returns {Object} Result with geojson, fileName, and featureCount
 */
export async function loadCSV(file, progressCallback) {
    try {
        const fileName = file.name.toLowerCase();
        const fileType = fileName.endsWith('.racas') ? 'RACAS' : (fileName.endsWith('.txt') ? 'TXT' : 'CSV');
        progressCallback(10, `Reading ${fileType} file...`);

        // Read file as text
        const text = await file.text();

        progressCallback(30, `Parsing ${fileType} data...`);

        // Parse CSV/TXT
        const csvData = parseCSV(text);

        if (csvData.rows.length === 0) {
            throw new Error(`No data rows found in ${fileType} file`);
        }

        progressCallback(50, 'Detecting coordinate fields...');

        // Detect coordinate fields
        const { latField, lonField } = detectCoordinateFields(csvData.headers);

        if (!latField || !lonField) {
            const availableFields = csvData.headers.join(', ');
            throw new Error(
                `Could not detect coordinate fields.\n\n` +
                `Available fields: ${availableFields}\n\n` +
                `Looking for: ${LATITUDE_FIELDS.join(', ')} and ${LONGITUDE_FIELDS.join(', ')}`
            );
        }

        console.log(`Detected coordinates: ${latField} (latitude), ${lonField} (longitude)`);

        progressCallback(70, 'Converting to GeoJSON...');

        // Convert to GeoJSON
        const geojson = csvToGeoJSON(csvData, latField, lonField);

        if (geojson.features.length === 0) {
            throw new Error(`No valid features could be created from ${fileType} data`);
        }

        progressCallback(90, 'Processing complete...');

        return {
            geojson,
            fileName: file.name.replace(/\.(csv|txt|racas)$/i, ''),
            featureCount: geojson.features.length,
            coordinateFields: { latField, lonField }
        };

    } catch (error) {
        console.error('Error loading file:', error);
        throw error;
    }
}

/**
 * Setup drag and drop for CSV files
 * @param {HTMLElement} dropArea - Drop area element
 * @param {Function} handleFile - File handler callback
 */
export function setupCSVDragAndDrop(dropArea, handleFile) {
    // Prevent default drag behaviors
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        dropArea.addEventListener(eventName, preventDefaults, false);
    });

    function preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    // Highlight drop area
    ['dragenter', 'dragover'].forEach(eventName => {
        dropArea.addEventListener(eventName, () => {
            dropArea.classList.add('drag-over');
        }, false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
        dropArea.addEventListener(eventName, () => {
            dropArea.classList.remove('drag-over');
        }, false);
    });

    // Handle dropped files
    dropArea.addEventListener('drop', (e) => {
        const dt = e.dataTransfer;
        const files = dt.files;

        if (files.length > 0) {
            handleFile(files[0]);
        }
    }, false);
}
